import type { LiveAvatar, LiveV1VoiceLibrary } from '@/app/types';
import { reportEvent } from '@/app/utils';

export function getAvatarVoiceLibrary(role: LiveAvatar): LiveV1VoiceLibrary | undefined {
  return (role as LiveAvatar & { voice_library?: LiveV1VoiceLibrary }).voice_library;
}

function trimUri(uri?: string): string {
  return uri?.trim() ?? '';
}

export function isDirectDisplayImageUrl(uri: string): boolean {
  const trimmed = trimUri(uri);
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  );
}

export function isSsuploadUri(uri: string): boolean {
  return trimUri(uri).startsWith('ssupload:');
}

function getAvatarPreviewImageCandidates(role: LiveAvatar): string[] {
  const imageUri = trimUri(role.image_uri);
  const liveImage = trimUri(role.live_image);
  const profilePhotoUri = trimUri(role.profile_photos?.[0]?.uri);
  const profilePhotoUris = (role.profile_photo_uris ?? []).map(trimUri).filter(Boolean);

  if (isUserBannerAvatar(role)) {
    return [profilePhotoUri, ...profilePhotoUris, liveImage, imageUri].filter(Boolean);
  }

  return [imageUri, liveImage, profilePhotoUri, ...profilePhotoUris].filter(Boolean);
}

/** 个人主页照片：profile_photos / profile_photo_uris */
export function getAvatarProfilePhotoImage(role: LiveAvatar): string {
  const profilePhotoUri = trimUri(role.profile_photos?.[0]?.uri);
  if (profilePhotoUri) {
    return profilePhotoUri;
  }

  const profilePhotoUris = (role.profile_photo_uris ?? []).map(trimUri).filter(Boolean);
  return profilePhotoUris[0] ?? '';
}

/** Banner 卡片展示图：优先个人主页照片，缺失时再回退主体图 */
export function getBannerAvatarCardImage(role: LiveAvatar): string {
  return getAvatarProfilePhotoImage(role) || trimUri(role.image_uri) || trimUri(role.live_image) || '';
}

export function getAvatarPreviewImage(role: LiveAvatar): string {
  return getAvatarPreviewImageCandidates(role)[0] ?? '';
}

export function getAvatarRoleKey(role: LiveAvatar): string {
  return role.id ?? role.code ?? role.name ?? '';
}

export function getAvatarRoleSubtitle(role: LiveAvatar): string {
  const name = role.name?.trim() ?? '';
  // 依次尝试：个人主页照片描述 → 人物介绍 description → 身份职业 → persona，取第一个非空且不等于名字的。
  // 自建角色（own）不写照片描述，会自然落到后面的字段。
  const subtitle =
    role.profile_photos?.[0]?.description?.trim() ||
    role.description?.trim() ||
    role.attrs?.identity_and_occupation?.trim() ||
    role.persona?.trim() ||
    '';
  if (!subtitle || subtitle === name) {
    return '';
  }
  return subtitle;
}

export function isUserBannerAvatar(role: LiveAvatar): boolean {
  return role.avatar_type === 'user';
}

interface StreamElementClickProps {
  element_name: string;
  stream_character_name?: string;
  choose_voice_name?: string;
}

/** vidu-stream 页统一埋点：空值字段（stream_character_name / choose_voice_name）不上报。 */
export function reportStreamElementClick(props: StreamElementClickProps) {
  const payload: Record<string, string | boolean | number> = { element_name: props.element_name };
  if (props.stream_character_name) {
    payload.stream_character_name = props.stream_character_name;
  }
  if (props.choose_voice_name) {
    payload.choose_voice_name = props.choose_voice_name;
  }
  reportEvent('stream_element_click', payload);
}
